import React from "react"
import { AgGridReact } from "ag-grid-react"
import { ColDef, GridOptions, RowClickedEvent, ModuleRegistry, GridReadyEvent } from "ag-grid-community"
import { AllCommunityModule } from "ag-grid-community"
import "ag-grid-community/styles/ag-grid.css"
import "ag-grid-community/styles/ag-theme-alpine.css"
import Pagination from "../Pagination/Pagination"
import { Box } from "@mui/material"
import data_loader from "../../assets/images/data_loader.gif"
import CustomHeader from "./Common/CustomHeader"
import OverflowTooltipCell from "./Common/OverFlowTooltipCell"
import "./agGridTable.scss"

ModuleRegistry.registerModules([AllCommunityModule])

interface PaginationProps {
    TotalPage: number
    pageNo: number
    setPageNo: (page: number) => void
    selectedValue: number
    setSelectedValue: (value: number) => void
    expandPagination?: boolean
}

export interface CommonAgGridProps<TData = any> {
    rowData: TData[]
    columnDefs: ColDef<TData>[]
    gridHeight?: string
    defaultColDef?: ColDef
    rowSelection?: GridOptions["rowSelection"]
    onRowClicked?: (event: RowClickedEvent<TData>) => void
    gridReady?: (event: GridReadyEvent<TData>) => void
    pagination?: PaginationProps
    autoWidth?: string
    autoHeight?: string
    height?: string
    loading?: boolean
    rowHeight?: number
}

const CommonAgGrid = <TData,>({
    rowData,
    columnDefs,
    gridHeight = "500px",
    defaultColDef = {
        sortable: true,
        filter: false,
        floatingFilter: false,
        resizable: true,
        menuTabs: ["generalMenuTab"],
        suppressHeaderMenuButton: false,
        suppressHeaderFilterButton: false,
        // flex: 1,
    },
    pagination,
    rowSelection = "single",
    onRowClicked,
    gridReady,
    height,
    autoHeight,
    autoWidth,
    loading,
    rowHeight = 36,
}: CommonAgGridProps<TData>) => {
    const [gridApi, setGridApi] = React.useState<any>(null)
    const [gridColumnApi, setGridColumnApi] = React.useState<any>(null)

    const getRowHeight = () => rowHeight

    const onGridReadyHandler = (params: any) => {
        setGridApi(params.api)
        setGridColumnApi(params.columnApi)
        if (gridReady) gridReady(params)
    }

    const columnDefsWithCustomHeader = React.useMemo(() => {
        return columnDefs.map(col => ({
            ...col,
            sortable: col.sortable || false,
            resizable: true,
            minWidth: 130,
            headerComponent: "customHeader",
            cellRenderer: col.cellRenderer || (typeof col.field === "string" ? OverflowTooltipCell : col.cellRenderer),
            headerStyle: {
                borderRight: "1px solid var(--ag-border-color)",
                fontSize: "12px",
            },
            cellStyle: {
                borderRight: "1px solid var(--ag-border-color)",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
            },
            comparator:
                col.sortable && !col.comparator
                    ? (valueA: any, valueB: any) => {
                          const isNull = (val: any) =>
                              val == null || val === "-" || (typeof val === "string" && val.trim() === "-")

                          const aNull = isNull(valueA)
                          const bNull = isNull(valueB)

                          if (aNull && bNull) return 0
                          if (aNull && !bNull) return 1 // null/"-" is considered greater and goes after non-null
                          if (!aNull && bNull) return -1

                          // Compare numbers
                          if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
                              return Number(valueA) - Number(valueB)
                          }

                          // Compare dates (ISO or Date parseable strings)
                          const dateA = Date.parse(valueA)
                          const dateB = Date.parse(valueB)
                          if (!isNaN(dateA) && !isNaN(dateB)) {
                              return dateA - dateB
                          }

                          // Fallback to string comparison (case insensitive)
                          const strA = String(valueA).toLowerCase()
                          const strB = String(valueB).toLowerCase()
                          if (strA < strB) return -1
                          if (strA > strB) return 1
                          return 0
                      }
                    : col.comparator,
        }))
    }, [columnDefs])
    return (
        <Box className="w-100 ag-theme-alpine" style={{ height: gridHeight }}>
            {loading ? (
                <div className={"loader_container"}>
                    <img src={data_loader} alt="loading" width={"150"} />
                </div>
            ) : (
                <>
                    <div className="h-92 w-100">
                        <AgGridReact<TData>
                            domLayout="normal"
                            rowData={rowData}
                            columnDefs={columnDefsWithCustomHeader}
                            defaultColDef={defaultColDef}
                            components={{
                                customHeader: CustomHeader,
                                overflowTooltipCell: OverflowTooltipCell,
                            }}
                            getRowHeight={getRowHeight}
                            headerHeight={38}
                            onGridReady={onGridReadyHandler}
                            suppressMovableColumns={false}
                            suppressHorizontalScroll={false}
                            enableRowPinning={true}
                            suppressRowHoverHighlight={true}
                            suppressCellFocus={true}
                        />
                    </div>
                    {pagination && (
                        <Pagination
                            totalPage={pagination.TotalPage}
                            pageNo={pagination.pageNo}
                            setPageNo={pagination.setPageNo}
                            setPageSize={pagination.setSelectedValue}
                            pageSize={pagination.selectedValue}
                            expandPagination={pagination.expandPagination}
                        />
                    )}
                </>
            )}
        </Box>
    )
}

export default CommonAgGrid
